import { JsonType } from "@/hooks/useFetch";
import {
  castDataToVersion,
  columnIndexes,
  detectDatasetVersion,
  SheetWithIndexes,
} from "@/lib/PreparedDataset";
import { parse } from "date-fns";
import { formatDate } from "./formatters";

type ValidatedCell = {
  value: string;
  error?: string;
};

type ValidatedDatasetItem = {
  id: string;
  cells: ValidatedCell[];
};

type ValidatedDataset = ValidatedDatasetItem[];

export function autoformatFields(data: SheetWithIndexes, version: "v1" | "v2"): SheetWithIndexes {
  const dateIndex = columnIndexes[version].date;
  const priceIndex = columnIndexes[version].price;

  return data.map((item) => {
    return {
      id: item.id,
      cells: item.cells.map((cell, i) => {
        if (i === dateIndex) {
          return validateDate(cell).value;
        }

        if (i === priceIndex) {
          return validatePrice(cell).value;
        }

        return cell;
      }),
    };
  });
}

export function validateDataset(data: SheetWithIndexes, version: "v1" | "v2"): ValidatedDataset {
  const dateIndex = columnIndexes[version].date;
  const priceIndex = columnIndexes[version].price;
  // console.log(dateIndex, priceIndex);

  return data.map((item) => {
    return {
      id: item.id,
      cells: item.cells.map((cell, i) => {
        if (cell === "" || cell === null || cell === undefined) {
          return { value: cell, error: "Cannot be empty" };
        }

        if (i === dateIndex) {
          return validateDate(cell);
        }

        if (i === priceIndex) {
          return validatePrice(cell);
        }

        return { value: cell };
      }),
    };
  });
}

export function validateDate(date: string) {
  const parsedDate = parseAnyDateFormat(date);

  if (!parsedDate) {
    return { value: date, error: "Invalid date" };
  }

  return { value: formatDate(parsedDate.getTime()) };
}

function parseAnyDateFormat(date: string): Date | null {
  // Remove any leading/trailing whitespace
  const trimmedDate = date.trim();

  // Try common US date formats
  const formats = [
    "yyyy-MM-dd", // 2024-03-14
    "MM/dd/yy", // 03/14/24
    "MM/dd/yyyy", // 03/14/2024
    "M/d/yy", // 3/14/24
    "M/d/yyyy", // 3/14/2024
    "MM-dd-yyyy", // 03-14-2024
    "M-d-yyyy", // 3-14-2024
    "MMM d, yyyy", // Mar 14, 2024
    "MMMM d, yyyy", // March 14, 2024
    "yyyy-MM-dd HH:mm:ss", // 2024-11-27 00:00:00
    "M/d/yyyy H:mm", // 6/1/2024 16:57
    "M/d/yyyy h:mm a", // 6/1/2024 4:57 PM
  ];

  for (const format of formats) {
    const parsedDate = parse(trimmedDate, format, new Date());
    if (!isNaN(parsedDate.getTime())) {
      return parsedDate;
    }
  }

  return null;
}

export function validatePrice(price: string): ValidatedCell {
  const parsedPrice = parseAnyPriceFormat(price);

  if (!parsedPrice) {
    return { value: price, error: "Invalid price" };
  }

  return { value: parsedPrice.toString() };
}

function parseAnyPriceFormat(price: string): number | null {
  // Remove whitespace
  const cleanPrice = price.trim();

  // Return null for empty strings
  if (!cleanPrice) return null;

  // Remove dollar signs and commas, handle negative amounts
  const normalized = cleanPrice
    .replace(/^\$/, "") // Remove leading dollar sign
    .replace(/,/g, ""); // Remove all commas

  // Parse the normalized string
  const parsedPrice = parseFloat(normalized);

  // Validate the result
  if (isNaN(parsedPrice)) return null;

  // Round to the nearest dollar
  const roundedPrice = Math.round(parsedPrice);

  return roundedPrice;
}

export function validateDatasetBasedOnVersion(data: JsonType) {
  const version = detectDatasetVersion(data);
  switch (version) {
    case "v1": {
      const castedData = castDataToVersion(data, version);
      return validateDataset(castedData, version);
    }
    case "v2": {
      const castedData = castDataToVersion(data, version);
      return validateDataset(castedData.data, version);
    }
    default: {
      return null;
    }
  }
}
